import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import LandingVideoExplainer from "@/components/LandingVideoExplainer";
import ChampionSpotlight from "@/components/ChampionSpotlight";
import LegalFooter from "@/components/LegalFooter";
import {
  GoogleLogo, Lightning, PlayCircle,
  GraduationCap, Briefcase, Handshake, ShieldCheck,
  Sparkle, ArrowRight, UsersThree, Buildings,
} from "@phosphor-icons/react";

/**
 * Landing — Tainapp public homepage.
 *
 * Structure:
 *  1. Sticky nav with sign-in
 *  2. Hero: headline + 2 CTAs (Join Tain, Explore Content)
 *  3. Six feature tiles covering core public functionality
 *  4. Community Highlights strip
 *  5. Footer with Terms of Service + contact
 */
export default function Landing() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [refCode] = useState(() => searchParams.get("ref") || "");
  // Legacy share URLs used `/?post=<id>` — redirect to the new deep-linked
  // feed route once the user is authed.
  const legacyPostId = searchParams.get("post") || searchParams.get("video_id");

  useEffect(() => {
    if (loading) return;
    if (user) {
      if (legacyPostId) {
        navigate(`/feed?video_id=${encodeURIComponent(legacyPostId)}`, { replace: true });
      } else {
        navigate("/feed");
      }
    }
  }, [user, loading, navigate, legacyPostId]);

  const handleGoogleLogin = () => {
    if (refCode) sessionStorage.setItem("tain_ref_code", refCode);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS
    const feedTarget = legacyPostId ? `/feed?video_id=${encodeURIComponent(legacyPostId)}` : "/feed";
    const redirectUrl = window.location.origin + feedTarget;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleExplore = () => {
    if (refCode) sessionStorage.setItem("tain_ref_code", refCode);
    const feedTarget = legacyPostId ? `/feed?video_id=${encodeURIComponent(legacyPostId)}` : "/feed";
    const redirectUrl = window.location.origin + feedTarget;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const FEATURES = [
    {
      icon: PlayCircle, tint: "from-blue-500 to-cyan-500",
      title: "Digital Content Feed",
      desc: "Vertical HD feed of videos, music and stories from independent creators. Discover fresh voices every scroll.",
    },
    {
      icon: UsersThree, tint: "from-fuchsia-500 to-pink-500",
      title: "Find Friends & Study Mates",
      desc: "Post what you're looking for — friendship, mentorship, study mate or business networking — and match with real, verified users near you.",
    },
    {
      icon: Buildings, tint: "from-emerald-500 to-teal-500",
      title: "Verified Community",
      desc: "Every profile is real. Grow trusted circles, meet accountable neighbours, and build with people you can actually rely on.",
    },
    {
      icon: GraduationCap, tint: "from-indigo-500 to-blue-500",
      title: "School Enhancement System",
      desc: "Full portals for parents, teachers and headteachers. Report cards, grading, promotions & real-time academic history.",
    },
    {
      icon: Briefcase, tint: "from-amber-500 to-yellow-500",
      title: "Jobs Portal",
      desc: "Post openings, screen candidates and message applicants inside a purpose-built careers marketplace.",
    },
    {
      icon: Handshake, tint: "from-violet-500 to-indigo-500",
      title: "Business Partnerships",
      desc: "Global business partnership directory with request flows, chat and vetted opportunity discovery.",
    },
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white overflow-hidden relative">
      {/* Ambient gradient blobs */}
      <div className="absolute -top-32 -left-32 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute top-1/2 -right-32 w-96 h-96 bg-emerald-500/15 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-1/3 w-[28rem] h-[28rem] bg-fuchsia-500/10 rounded-full blur-3xl pointer-events-none" />

      {/* Nav */}
      <nav className="relative z-10 flex justify-between items-center px-6 md:px-12 py-6">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-500 to-emerald-500 flex items-center justify-center">
            <Lightning size={22} weight="fill" className="text-white" />
          </div>
          <span className="font-heading text-2xl font-black tracking-tight">Tain</span>
        </div>
        <Button
          data-testid="nav-login-btn"
          onClick={handleGoogleLogin}
          className="bg-white text-black hover:bg-white/90 font-semibold"
        >
          Sign in
        </Button>
      </nav>

      {/* Hero */}
      <main className="relative z-10 max-w-6xl mx-auto px-6 md:px-12 pt-6 md:pt-12 pb-24">
        <LandingVideoExplainer />
        <ChampionSpotlight />
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs uppercase tracking-[0.2em] font-bold text-white/70 mb-6">
              <Sparkle size={14} weight="fill" className="text-emerald-400" />
              One ecosystem · Verified users
            </div>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95]">
              Connect, Learn, Build{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-emerald-400">
                & Grow
              </span>
              <br />— All in One Ecosystem.
            </h1>
            <p className="mt-6 text-lg text-white/70 max-w-lg leading-relaxed">
              Tainapp brings social feeds, friendships, verified business
              partnerships, jobs and school management into a single, unified
              digital hub.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button
                data-testid="hero-cta-join-tain"
                onClick={handleGoogleLogin}
                size="lg"
                className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:brightness-110 text-black font-heading font-bold h-12 px-6 gap-2"
              >
                <GoogleLogo size={18} weight="bold" />
                Join Tainapp Today — It's Free
              </Button>
              <Button
                data-testid="hero-cta-explore"
                onClick={handleExplore}
                size="lg"
                variant="outline"
                className="bg-transparent border-white/20 hover:bg-white/5 text-white font-semibold h-12 px-6 gap-2"
              >
                <PlayCircle size={18} weight="fill" />
                Explore Content
              </Button>
            </div>
            <p className="mt-4 text-xs text-white/50">
              Secure Data Protection · Verified Users · Community-First Guidelines
            </p>
          </div>

          {/* Right — community highlight card */}
          <div className="relative">
            <div className="glass rounded-2xl p-6 md:p-8 space-y-5">
              <div className="flex items-center justify-between pb-4 border-b border-white/10">
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] font-bold text-white/50">Community first</p>
                  <p className="font-heading text-2xl md:text-3xl font-black text-emerald-400 mt-1">Real people · Real trust</p>
                </div>
                <UsersThree size={40} weight="bold" className="text-emerald-400" />
              </div>
              {[
                { label: "Verified community", value: "KYC-first", icon: Buildings },
                { label: "Content pillars", value: "8 metrics", icon: Sparkle },
                { label: "Verified profiles", value: "KYC required", icon: ShieldCheck },
              ].map((s, i) => (
                <div key={i} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center">
                      <s.icon size={18} weight="bold" className="text-white/80" />
                    </div>
                    <span className="text-white/70 text-sm">{s.label}</span>
                  </div>
                  <span className="font-heading font-bold text-white text-sm">{s.value}</span>
                </div>
              ))}
              <div className="text-[10px] uppercase tracking-widest text-white/40 pt-3 border-t border-white/10">
                One community, one profile, one everyday hub
              </div>
            </div>
          </div>
        </div>

        {/* Feature tiles */}
        <section className="mt-24">
          <div className="mb-10 text-center max-w-2xl mx-auto">
            <h2 className="font-heading text-3xl md:text-4xl font-black tracking-tight">
              Everything Tainapp does, in one place.
            </h2>
            <p className="text-white/60 mt-3">
              Six modules under one profile — social feed, verified
              community, friendships, schools, jobs and business partnerships.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map((f, i) => (
              <div
                key={i}
                data-testid={`landing-feature-${i}`}
                className="glass rounded-2xl p-6 hover:bg-white/[0.05] transition-colors"
              >
                <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${f.tint} flex items-center justify-center mb-4`}>
                  <f.icon size={22} weight="fill" className="text-white" />
                </div>
                <h3 className="font-heading font-bold text-lg mb-2">{f.title}</h3>
                <p className="text-white/60 text-sm leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Secondary CTA strip */}
        <section className="mt-20">
          <div className="rounded-2xl bg-gradient-to-r from-blue-600/20 via-emerald-500/15 to-fuchsia-500/15 border border-white/10 p-8 md:p-10 flex flex-col md:flex-row md:items-center gap-6 justify-between">
            <div className="max-w-xl">
              <h3 className="font-heading text-2xl md:text-3xl font-black tracking-tight">
                Ready to join the Tainapp community?
              </h3>
              <p className="text-white/70 mt-2">
                Sign up in seconds. Meet creators, schools, employers and
                business partners — all in one verified digital hub.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 shrink-0">
              <Button
                data-testid="cta-strip-join"
                onClick={handleGoogleLogin}
                className="bg-gradient-to-r from-emerald-500 to-teal-500 text-black font-heading font-bold h-12 px-6 gap-2"
              >
                <GoogleLogo size={18} weight="bold" /> Join Tainapp Today
                <ArrowRight size={16} weight="bold" />
              </Button>
            </div>
          </div>
        </section>
      </main>

      <LegalFooter />
    </div>
  );
}
